import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { Pastille } from '../ui/Primitives';
import { jourContrat, type ContratLigne } from '../../lib/useContrats';

function Rubrique({ libelle, valeur }: { libelle: string; valeur: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {libelle}
      </dt>
      <dd className="mt-0.5 break-words text-sm text-codialis-navy">{valeur}</dd>
    </div>
  );
}

function Section({
  titre,
  children,
  ton = 'clair',
}: {
  titre: string;
  children: React.ReactNode;
  ton?: 'clair' | 'alerte';
}): JSX.Element {
  return (
    <section
      className={`rounded-xl border p-4 ${
        ton === 'alerte' ? 'border-red-100 bg-red-50/60' : 'border-gray-100 bg-codialis-sand/40'
      }`}
    >
      <h3 className="font-display text-sm font-bold text-codialis-navy">{titre}</h3>
      <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</dl>
    </section>
  );
}

/**
 * Fiche d'un contrat d'apprentissage : ce que la liste n'affiche pas, rangé par
 * rubriques plutôt qu'aligné en colonnes.
 *
 * La section « Rupture » n'apparaît que si une rupture existe, et elle ne
 * remplace rien : les dates initiales du contrat restent affichées au-dessus.
 * Une rupture appartient à l'histoire du contrat, elle ne l'efface pas.
 *
 * Rémunération, maître d'apprentissage et rubriques du CERFA restent dans la
 * fenêtre de contrôle du CERFA, qui est leur lieu de saisie : les recopier ici
 * ferait deux écrans à tenir d'accord.
 */
export function FicheContrat({
  contrat,
  onFermer,
  onOuvrirCerfa,
  onOuvrirDocumentCerfa,
  onOuvrirDocumentRupture,
}: {
  contrat: ContratLigne | null;
  onFermer: () => void;
  onOuvrirCerfa: (immersionId: string) => void;
  /** Ouvre le CERFA produit, en lecture : corriger se décide sur le document. */
  onOuvrirDocumentCerfa: (documentId: string) => void;
  onOuvrirDocumentRupture: (documentId: string) => void;
}): JSX.Element | null {
  if (!contrat) return null;
  const rupture = contrat.rupture;

  return (
    <Modale
      titre={contrat.candidat}
      sousTitre={`Contrat d'apprentissage · ${contrat.entreprise}`}
      ouverte
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <Pastille texte={contrat.etatLibelle} ton={contrat.etatTon} />
          {contrat.etatDetail ? (
            <span className="text-xs text-codialis-gray">{contrat.etatDetail}</span>
          ) : null}
          <Pastille texte={contrat.siteCode} ton="info" />
        </div>

        <Section titre="Apprenti(e)">
          <Rubrique libelle="Nom et prénom" valeur={contrat.candidat} />
          <Rubrique libelle="Antenne" valeur={contrat.siteCode} />
        </Section>

        <Section titre="Entreprise">
          <Rubrique libelle="Dénomination" valeur={contrat.entreprise} />
          <Rubrique
            libelle="Fin d’immersion"
            valeur={contrat.sansImmersion ? 'Sans immersion' : jourContrat(contrat.dateFinImmersion)}
          />
        </Section>

        <Section titre="Formation">
          <Rubrique libelle="Formation visée" valeur={contrat.formation ?? 'Non renseignée'} />
        </Section>

        <Section titre="Contrat">
          <Rubrique libelle="Début" valeur={jourContrat(contrat.dateDebut)} />
          <Rubrique
            libelle={rupture ? 'Fin initialement prévue' : 'Fin prévue'}
            valeur={jourContrat(contrat.dateFin)}
          />
          <Rubrique libelle="Signé le" valeur={jourContrat(contrat.dateSignature)} />
          <Rubrique
            libelle="Saisie CERFA"
            valeur={
              // Le CERFA se lit sur le document produit, pas sur l'existence du
              // contrat : un contrat né en brouillon depuis le dossier n'en a
              // pas encore.
              contrat.documentCerfaId
                ? 'CERFA généré'
                : contrat.saisieCerfaLe
                  ? `Brouillon enregistré le ${jourContrat(contrat.saisieCerfaLe)}`
                  : contrat.contratId
                    ? 'CERFA non généré'
                    : 'Non entamée'
            }
          />
        </Section>

        {rupture && (
          <Section titre="Rupture du contrat" ton="alerte">
            <Rubrique
              libelle="Date de rupture"
              valeur={rupture.dateRupture ? jourContrat(rupture.dateRupture) : 'À préciser'}
            />
            <Rubrique libelle="Motif" valeur={rupture.motif} />
            <Rubrique
              libelle="Formation"
              valeur={
                rupture.formationMaintenue
                  ? 'Maintenue — recherche d’une nouvelle entreprise'
                  : 'Non maintenue — le parcours prend fin'
              }
            />
            <Rubrique
              libelle="Enregistrée le"
              valeur={
                rupture.saisiePar
                  ? `${jourContrat(rupture.enregistreeLe)} par ${rupture.saisiePar}`
                  : jourContrat(rupture.enregistreeLe)
              }
            />
            {rupture.commentaire && (
              <div className="sm:col-span-2">
                <Rubrique libelle="Commentaire" valeur={rupture.commentaire} />
              </div>
            )}
            {rupture.documentId && (
              <div className="sm:col-span-2">
                <button
                  type="button"
                  onClick={() => onOuvrirDocumentRupture(rupture.documentId!)}
                  className="inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-codialis-sky hover:underline"
                >
                  <FileText className="h-4 w-4" aria-hidden />
                  Prévisualiser le document de rupture
                </button>
              </div>
            )}
          </Section>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
          <Link
            to={`/adm-dossier/${contrat.apprenantId}`}
            className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
          >
            Voir le dossier
          </Link>
          <Link
            to={`/ped-repertoire/${contrat.entrepriseId}`}
            className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
          >
            Dossier entreprise
          </Link>
          {contrat.documentCerfaId && (
            <button
              type="button"
              onClick={() => onOuvrirDocumentCerfa(contrat.documentCerfaId!)}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
            >
              <FileText className="h-4 w-4" aria-hidden />
              Ouvrir le CERFA
            </button>
          )}
          {contrat.etat !== 'REFUSE' && (
            <button
              type="button"
              onClick={() => onOuvrirCerfa(contrat.id)}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
            >
              {contrat.contratId ? 'Modifier le CERFA' : 'Valider le contrat'}
            </button>
          )}
        </div>
      </div>
    </Modale>
  );
}
